import dayjs from "dayjs";

import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import HotelIcon from "@mui/icons-material/Hotel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ModeIcon from "@mui/icons-material/Mode";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

import InfiniteModeProInfo from "@/components/InfiniteModeProInfo";
import ProChip from "@/components/ProChip";
import { airlineBgColor } from "@/constants/config";
import { HOTEL_IMG } from "@/constants/hotels";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

import { IAlertItem } from "./utils";

interface Props {
  data: IAlertItem;
  onEdit: () => void;
  onDelete: () => void;
  onChangeStatus: (v: boolean) => Promise<void>;
  onView: () => void;
  onDetail: () => void;
  changeStatusLoading: boolean;
  onInfiniteModeChange: (v: boolean) => Promise<void>;
  onShowTips: () => void;
}

const AlertItem = (props: Props) => {
  const { data, changeStatusLoading } = props;

  const { isMobile } = usePlatform();

  const filterData = data.filterData;
  const theme = useThemeData();
  const [loading, setLoading] = useState(false);

  const store = useGlobalStoreContext();

  const renderRightContent = () => {
    if (!data.expired && data.targetData && !(data.valid && data.viewed)) {
      let formattedDate = undefined;
      if (data.targetData.version) {
        formattedDate = dayjs(parseInt(data.targetData.version) * 1000).format("YYYY/MM/DD");
      }
      return (
        <div className="flex items-center justify-between">
          <div className="space-y-[6px]">
            <div className="flex items-center space-x-[6px]">
              {data?.targetData?.lowest_miles?.program && (
                <Box
                  className="rounded-[3px] p-[6px] flex items-center w-[40px]"
                  sx={{
                    backgroundColor: theme.isDarkMode ? airlineBgColor : "transparent",
                    fontSize: 0,
                  }}
                >
                  <img
                    className="w-[100%]"
                    src={HOTEL_IMG[data?.targetData?.lowest_miles?.program]}
                    alt=""
                  />
                </Box>
              )}

              {data.targetData.lowest_miles?.miles && (
                <Typography fontSize={14}>{`${(+data.targetData.lowest_miles
                  ?.miles)?.toLocaleString()} pts`}</Typography>
              )}
              {!data.viewed && (
                <Chip
                  label={`New`}
                  size="small"
                  sx={{
                    borderRadius: "7px",
                  }}
                  color="primary"
                />
              )}
              {data.viewed && <Typography fontSize={14}>Viewed</Typography>}
            </div>

            {formattedDate && (
              <div className="ml-[6px]">
                <Typography fontSize={14}>Seen on: {formattedDate}</Typography>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center mt-[10px]">
              <Button variant="contained" onClick={props.onDetail}>
                Details
              </Button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <Typography className="!text-[14px]" color="grey">
            {data.valid
              ? "We're still in search of an eligible hotel."
              : "Switch on the alert so we will find your ideal hotel."}
          </Typography>
        </div>
      );
    }
  };

  const SwitchIcon = () => (
    <div
      className="flex items-center justify-center w-[20px] h-[20px] rounded-[50%]"
      style={{
        backgroundColor:
          data.valid && !changeStatusLoading
            ? theme.palette.primary.main
            : theme.palette.background.default,
      }}
    >
      {changeStatusLoading && <CircularProgress size={14} color="primary" thickness={6} />}
    </div>
  );

  const usedAlertCount =
    (dayjs(data.info.departureDateSec).startOf("day").diff(data.info.departureDate, "day") + 1) *
    data.info.departure.length *
    data.info.arrival.length;

  return (
    <Paper className="relative">
      <Chip
        size="small"
        className="absolute left-0 top-0 !rounded-none"
        color="primary"
        label={`alert count: ${usedAlertCount}`}
      />
      <Chip
        size="small"
        className="w-[130px] absolute right-0 top-0 !rounded-none !rounded-bl-3xl"
        color="primary"
        label={
          <div className="flex items-center space-x-[4px]">
            <HotelIcon sx={{ fontSize: "16px" }} />
            <span>hotel</span>
          </div>
        }
      />
      <div className="px-[15px] pt-[33px] pb-[15px] md:flex items-center justify-between">
        <img
          className="w-[100%] h-auto md:w-[200px] md:h-[133px] md:mr-[20px]"
          src={`https://img.awardtool.com/hotel-imgs/${data.info.departure[0]}.jpg`}
          alt=""
          onClick={props.onDetail}
        />
        <div className="md:flex-1">
          <div className="flex flex-wrap" onClick={props.onDetail}>
            <Typography>{(data.info.hotelName || []).join(",")}</Typography>
          </div>

          {isMobile ? (
            <div className="mt-[4px] mb-[2px]">
              <Typography fontSize={14}>
                First stay: {dayjs(data.info.departureDate).format("ddd, MMM DD YYYY")}
              </Typography>
              {data.info.departureDate !== data.info.departureDateSec && (
                <Typography fontSize={14}>
                  Last stay: {dayjs(data.info.departureDateSec).format("ddd, MMM DD YYYY")}
                </Typography>
              )}
            </div>
          ) : (
            <Typography fontSize={14} className="!mt-[4px] !mb-[2px]">
              <span>First stay: {dayjs(data.info.departureDate).format("ddd, MMM DD YYYY")} </span>
              {data.info.departureDate !== data.info.departureDateSec && (
                <span>
                  {" "}
                  - Last stay: {dayjs(data.info.departureDateSec).format("ddd, MMM DD YYYY")}
                </span>
              )}
            </Typography>
          )}

          {/* filter */}
          <div className="mt-[10px]">
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {filterData.max_miles && (
                <Chip
                  label={"Max points: " + `${Number(filterData.max_miles)?.toLocaleString()} pts`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </div>
        </div>

        <div className="mt-[20px] md:flex-1 md:mt-0 md:ml-[60px]">{renderRightContent()}</div>
      </div>
      <Divider />
      <div className="flex justify-between items-center p-[5px]">
        <div className="flex items-center">
          {data.expired ? (
            <div className="ml-[15px]">
              <Typography>Expired</Typography>
            </div>
          ) : (
            <>
              <Switch
                checkedIcon={<SwitchIcon />}
                icon={<SwitchIcon />}
                checked={data.valid}
                onChange={async (_, checked) => {
                  props.onChangeStatus(checked);
                }}
              />
              {data.valid ? "Alert On" : "Alert Off"}
            </>
          )}
        </div>
        <div className="flex items-center">
          <div className="flex justify-between w-[100%]">
            <FormControlLabel
              control={
                loading ? (
                  <div className="w-[42px] h-[42px] flex items-center justify-center">
                    <CircularProgress size={14} color="primary" thickness={6} />
                  </div>
                ) : (
                  <Checkbox
                    checked={data.infinite_mode}
                    onChange={async (_, checked) => {
                      if (!store.user_type) {
                        props.onShowTips();
                        return;
                      }

                      try {
                        setLoading(true);
                        await props.onInfiniteModeChange(checked);
                        setLoading(false);
                      } catch (error) {
                        setLoading(false);
                      }
                    }}
                  />
                )
              }
              label={
                <div className="flex items-center">
                  <span>{isMobile ? "Infinite" : "Infinite mode"}</span>
                  <ProChip />
                </div>
              }
            />
            <Tooltip sx={{ size: 10 }} title={<InfiniteModeProInfo />}>
              <InfoOutlinedIcon sx={{ fontSize: 16, margin: "auto", ml: -1.5 }} />
            </Tooltip>
          </div>
          <IconButton aria-label="edit" size="small" onClick={() => props.onEdit()}>
            <ModeIcon fontSize="inherit" />
          </IconButton>
          <IconButton aria-label="delete" size="small" onClick={() => props.onDelete()}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </div>
      </div>
    </Paper>
  );
};

export default AlertItem;
